/**
 * Générateur — Automatisme : manipulation de sommes et différences de nombres
 * relatifs.
 * Attendu ID : 1091 | Niveau : 4ème | Domaine : Opérations sur les nombres relatifs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 632 :
 * « Manipulation de sommes et différences de nombre relatifs. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * ENTRETIEN pur des objectifs de 5e (l. 531-536 : additionner, soustraire,
 * simplifier l'écriture, enchaîner). Rien de nouveau : « manipulation », c'est
 * calculer ET réécrire (enlever les parenthèses, transformer une différence
 * en somme). Pas de wrapper : les gens 5e 708-713 sont des objectifs
 * multi-cartes avec contextes ; ici un gen compact, trois cartes.
 * Frontières : ni produit ni quotient (l. 640-642), pas d'enchaînement avec
 * × (l. 643), pas de lettre, pas de décimal (choix mode auto : DEPP 4e 2023,
 * banque et groupe « à besoins » Eduscol travaillent des ENTIERS ; les
 * décimaux à une décimale restent À ARBITRER par le prof).
 *
 * Périmètre observé MathsMentales (page 4e ; S. Cogez, Apache 2.0, relevé
 * 2026-09-12) : sommes de 3 à 4 relatifs entre −10 et 10 sans 0 avec
 * parenthèses ; sommes algébriques sans parenthèses entre −20 et 20 ;
 * différences (a) − (b). Repris tels quels, plus ce que la banque n'a pas :
 * la RÉÉCRITURE (carte 3), qui est le mot du programme.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX
 *     dans un ordre fixe, une par geste : somme de deux termes avec
 *     parenthèses (au moins un négatif) ; somme de deux termes en écriture
 *     simplifiée commençant par un négatif (−5 + 7, −5 − 7) ; différence avec
 *     le piège du double moins (7 − (−5), −7 − (−5)) ou a − b avec a < b ;
 *     somme de 3 ou 4 termes (−10..10), avec ou sans parenthèses. Quatre
 *     résultats distincts par tirage. Réponse : le résultat, tiret clavier
 *     pour le signe moins, 0 s'il est nul ; « +2 » accepté par la tolérance
 *     numérique (2 = +2).
 *   Carte 2 « Quel résultat ? » (resultat_qcm_unique) : UNE somme ou
 *     différence de deux termes, six formes en rotation, QUATRE choix qui
 *     sont toujours les quatre nombres ±(a + b) et ±(a − b) : la clé est l'un
 *     d'eux, les trois autres sont les familles d'erreurs attestées (DEPP 4e
 *     2023, q.5 « −5 + 7 » et q.14 « 7 − (−5) »), nommées par forme dans
 *     REGLES_DISTRACTEURS_QCM. Distincts par construction (a ≠ b).
 *   Carte 3 « Écrire sans parenthèses » (sens_texte_trou_drag) : une somme de
 *     trois termes avec parenthèses, dont au moins un « − (−x) », à réécrire
 *     en écriture simplifiée : le premier terme est donné, les deux suivants
 *     sont des étiquettes « + 5 » / « - 8 » à glisser, les leurres sont les
 *     deux étiquettes de signe contraire. Le geste seul, sans calcul
 *     (diagnostic, comme la carte « transformer » de 710).
 *
 * INVARIANTS : entiers non nuls, |terme| ≤ 20 à deux termes, ≤ 10 à trois ou
 * quatre ; à la carte 3 les trois valeurs absolues sont distinctes (aucune
 * étiquette en double) ; chaque égalité est re-vérifiée par un relecteur
 * d'écriture indépendant des termes (throw sinon).
 * SACRIFIÉ (fiche d'aide à venir, « je pars de −3, j'avance de 8 ») : toute
 * ligne à DEUX termes dont les termes, une fois la différence transformée en
 * somme, sont {−3 ; +8} : −3 + 8, 8 + (−3), −3 − (−8), dans les deux
 * cartes à deux termes (invariant auto-testé).
 */
import {
    randomChoice, randInt,
    makeQcm, makeTexteTrou, tex, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 1091;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quel résultat ?';
const L_SENS = 'Écrire sans parenthèses';

const MAX_DEUX = 20;      // deux termes : −20..20
const MAX_PLUSIEURS = 10; // trois ou quatre termes : −10..10

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les deux termes de la fiche d'aide, sacrifiés sous toutes leurs écritures. */
export const TERMES_SACRIFIES = [-3, 8];

/** Un entier non nul de valeur absolue 1..max, signe imposé ou tiré. */
const entier = (max, signe = randomChoice([1, -1])) => signe * randInt(1, max);

/**
 * Les termes d'une ligne à deux termes vus comme une SOMME : a + b → [a, b],
 * a − b → [a, −b]. C'est sur cette forme que porte le sacrifice.
 */
const estSacrifiee = (termesSomme) => {
    if (termesSomme.length !== 2) return false;
    const [x, y] = termesSomme;
    return (x === TERMES_SACRIFIES[0] && y === TERMES_SACRIFIES[1]) || (x === TERMES_SACRIFIES[1] && y === TERMES_SACRIFIES[0]);
};

/** Garde-fou : entier non nul dans la borne (assert). */
const verifierTerme = (t, max) => {
    if (!Number.isInteger(t) || t === 0 || Math.abs(t) > max) throw new Error(`gen_1091 : terme hors domaine ${t}`);
    return t;
};

// ─── Écritures ────────────────────────────────────────────────────────────
//
// Une « opération » est { termes, ops } : termes[0] puis, pour i ≥ 1,
// ops[i-1] ∈ '+' | '-' appliqué à termes[i]. Sa valeur : termes[0] ± termes[i].
// Le tiret clavier « - » sert partout (KaTeX le rend en signe moins, et
// c'est ce que l'élève tape).

const valeur = ({ termes, ops }) => termes.reduce((acc, t, i) => (i === 0 ? t : (ops[i - 1] === '+' ? acc + t : acc - t)), 0);

/** Les termes de l'opération vus comme une somme (a − b = a + (−b)). */
const enSomme = ({ termes, ops }) => termes.map((t, i) => (i === 0 || ops[i - 1] === '+' ? t : -t));

/** Premier terme d'une écriture simplifiée : « -3 » ou « 7 », jamais de parenthèses ni de signe +. */
const premier = (t) => String(t);

/**
 * Écriture avec parenthèses : chaque terme négatif est entre parenthèses,
 * le premier compris (banque, 5e), un positif est nu : « 7 + (-5) - (-2) »,
 * « (-7) + 5 ».
 */
const ecrireParentheses = ({ termes, ops }) => {
    const p = (t) => (t < 0 ? `(${t})` : String(t));
    return termes.map((t, i) => (i === 0 ? p(t) : ` ${ops[i - 1]} ${p(t)}`)).join('');
};

/**
 * Écriture simplifiée d'une SOMME (ops tous « + ») : « -3 + 8 - 6 ». Une
 * différence a − (−b) n'a pas d'écriture simplifiée sans transformation :
 * on la garde en parenthèses.
 */
const ecrireSimplifiee = (termesSomme) => termesSomme
    .map((t, i) => (i === 0 ? premier(t) : ` ${t < 0 ? '-' : '+'} ${Math.abs(t)}`))
    .join('');

/** Une opération, écrite selon sa forme. */
const ecrire = (op, forme) => (forme === 'simplifiee' ? ecrireSimplifiee(enSomme(op)) : ecrireParentheses(op));

/** Relit une écriture caractère par caractère et la calcule (le second chemin de l'assert). */
const relire = (ecriture) => {
    const s = ecriture.replace(/\s+/g, '');
    let i = 0;
    const lireTerme = () => {
        let paren = false;
        if (s[i] === '(') { paren = true; i++; }
        const m = s.slice(i).match(/^[+-]?\d+/);
        if (!m) throw new Error(`gen_1091 : écriture illisible ${ecriture}`);
        i += m[0].length;
        if (paren) { if (s[i] !== ')') throw new Error(`gen_1091 : parenthèse ${ecriture}`); i++; }
        return parseInt(m[0], 10);
    };
    let total = lireTerme();
    while (i < s.length) {
        const opc = s[i];
        if (opc !== '+' && opc !== '-') throw new Error(`gen_1091 : opérateur ${ecriture}`);
        i++;
        const t = lireTerme();
        total = opc === '+' ? total + t : total - t;
    }
    return total;
};

const verifierOperation = (op, forme, max) => {
    op.termes.forEach((t) => verifierTerme(t, max));
    const ecriture = ecrire(op, forme);
    if (relire(ecriture) !== valeur(op)) throw new Error(`gen_1091 : écriture et valeur en désaccord ${ecriture}`);
    if (estSacrifiee(enSomme(op))) throw new Error('gen_1091 : termes sacrifiés tirés');
    return ecriture;
};

// ─── Tirages ──────────────────────────────────────────────────────────────

/** Somme de deux termes avec parenthèses, au moins un négatif. */
const tirerSommeParentheses = () => {
    for (let essai = 0; essai < 60; essai++) {
        const termes = [entier(MAX_DEUX), entier(MAX_DEUX)];
        if (termes.every((t) => t > 0)) continue;
        const op = { termes, ops: ['+'] };
        if (!estSacrifiee(enSomme(op))) return op;
    }
    return { termes: [-7, -5], ops: ['+'] };
};

/** Somme de deux termes en écriture simplifiée, premier terme négatif : −5 + 7 (deux fois sur trois) ou −5 − 7. */
const tirerSommeSimplifiee = () => {
    for (let essai = 0; essai < 60; essai++) {
        const a = entier(MAX_DEUX, -1);
        const b = entier(MAX_DEUX, randInt(1, 3) === 1 ? -1 : 1);
        if (Math.abs(a) === Math.abs(b)) continue; // −5 + 5 = 0 : c'est 1092 (somme des opposés)
        const op = { termes: [a, b], ops: ['+'] };
        if (!estSacrifiee(enSomme(op))) return op;
    }
    return { termes: [-9, 4], ops: ['+'] };
};

/** Différence : a − (−b), −a − (−b) (le double moins), ou a − b avec a < b. */
const tirerDifference = () => {
    for (let essai = 0; essai < 60; essai++) {
        const a = randInt(1, MAX_DEUX);
        const b = randInt(1, MAX_DEUX);
        if (a === b) continue;
        const forme = randomChoice(['double_moins', 'double_moins', 'double_moins_negatif', 'petit_moins_grand']);
        let op;
        if (forme === 'double_moins') op = { termes: [a, -b], ops: ['-'] };
        else if (forme === 'double_moins_negatif') op = { termes: [-a, -b], ops: ['-'] };
        else op = { termes: [Math.min(a, b), Math.max(a, b)], ops: ['-'] };
        if (!estSacrifiee(enSomme(op))) return op;
    }
    return { termes: [7, -5], ops: ['-'] };
};

/** Somme de trois ou quatre termes dans −10..10, des deux signes (sinon c'est du 6e), valeurs absolues distinctes. */
const tirerSommePlusieurs = () => {
    const n = randomChoice([3, 4]);
    for (let essai = 0; essai < 60; essai++) {
        const termes = Array.from({ length: n }, () => entier(MAX_PLUSIEURS));
        if (termes.every((t) => t > 0) || termes.every((t) => t < 0)) continue;
        if (new Set(termes.map(Math.abs)).size < n) continue;
        return { termes, ops: Array(n - 1).fill('+') };
    }
    return { termes: [-3, 8, -6, 2], ops: ['+', '+', '+'] };
};

// ─── Explications ─────────────────────────────────────────────────────────

const EXPLICATIONS = {
    distances_ajoutees: 'Les deux nombres ont été comptés comme s\'ils étaient tous les deux négatifs. Regarde le signe de chacun.',
    signes_ignores: 'Les signes ont été oubliés : on a additionné les distances à zéro.',
    signe_inverse: 'La distance est bonne, le signe non : le résultat prend le signe du nombre le plus loin de zéro.',
    moins_avale: 'Soustraire un nombre négatif, c\'est ajouter son opposé : 7 − (−5) = 7 + 5.',
    ordre_inverse: 'Les deux nombres ont été soustraits dans le mauvais ordre.',
    ecart_au_lieu_de_somme: 'Deux nombres négatifs s\'ajoutent : le résultat est encore plus loin de zéro.',
    ecart_sans_signe: 'Deux nombres négatifs s\'ajoutent, et le résultat est négatif.',
    signe_contraire: 'Soustraire un nombre, c\'est ajouter son opposé : le signe change.',
    regle: 'Pour additionner deux nombres de signes contraires, on soustrait les distances à zéro et on garde le signe du plus loin de zéro. Soustraire un nombre, c\'est ajouter son opposé.',
};

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

/** Le code commun d'un code du gen : ordre_inverse, ecart_sans_signe et signe_contraire ont été fusionnés. */
const CODE_COMMUN = { ordre_inverse: 'moins_avale', ecart_sans_signe: 'ecart_au_lieu_de_somme', signe_contraire: 'moins_avale' };
const commun = (code) => CODE_COMMUN[code] || code;
// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : un code ne porte qu'une seule erreur. Au QCM, la même
// famille ne casse pas la même règle selon la question : dans une addition à signes contraires
// (−a + b, a + (−b)), la règle des signes contraires ; dans une soustraction, « soustraire, c'est
// ajouter l'opposé » ; (−a) + (−b) a ses propres codes, sans règle lue pour l'instant.
const ADDITIONS = ['negatif_plus_positif', 'positif_plus_negatif'];
/** Le code d'une famille au QCM, selon la forme de la question (nom) et son écriture (forme). */
const codeQcm = (famille, nom, forme) => {
    const code = commun(famille);
    const addition = ADDITIONS.includes(nom);
    if (code === 'signe_inverse') return addition ? 'signe_inverse_addition' : 'signe_inverse';
    if (code === 'distances_ajoutees') return addition ? 'distances_ajoutees' : 'distances_ajoutees_soustraction';
    if (code === 'signes_ignores') {
        if (addition) return 'signes_ignores';
        return nom === 'deux_negatifs' && forme === 'parentheses' ? 'signes_ignores_deux_negatifs' : 'signes_ignores_soustraction';
    }
    if (code === 'ecart_au_lieu_de_somme') {
        if (forme === 'parentheses') return 'ecart_au_lieu_de_somme';
        // Relecture du 04/10/2026 (E52) : dans −8 − 4, « 4 » efface en plus le signe de −8, une autre erreur
        // que l'écart ; sans phrase validée, le choix reste proposé et part sans nom.
        return famille === 'ecart_sans_signe' ? null : 'ecart_au_lieu_de_somme_soustraction';
    }
    return code;
};

/** La forme de FORMES_QCM d'une ligne à deux termes, null hors des six formes. */
const formeDe = ({ termes: [x, y], ops: [o] }) => {
    if (o === '+') return x < 0 ? (y < 0 ? 'deux_negatifs' : 'negatif_plus_positif') : (y < 0 ? 'positif_plus_negatif' : null);
    if (y < 0) return x < 0 ? 'double_moins_negatif' : 'double_moins';
    return x > 0 && x < y ? 'petit_moins_grand' : null;
};

/** Les erreurs d'une ligne de la série : les familles du QCM à deux termes, signes oubliés et mauvais signe au-delà. */
const famillesLigne = (op) => {
    if (op.termes.length === 2) {
        const nom = formeDe(op);
        return nom ? FORMES_QCM[nom].codes(Math.abs(op.termes[0]), Math.abs(op.termes[1])) : [];
    }
    const v = valeur(op);
    return [[enSomme(op).reduce((acc, t) => acc + Math.abs(t), 0), 'signes_ignores'], ...(v !== 0 ? [[-v, 'signe_inverse']] : [])];
};

/** Une erreur par valeur fausse que les familles ne se disputent pas (ni la bonne réponse, ni 0, tapé faute de calcul). */
const erreursFlash = (items) => items.flatMap((it, trou) => {
    const cands = famillesLigne(it.op).map(([v, code]) => [v, commun(code)]).filter(([v]) => v !== it.resultat && v !== 0);
    return cands
        .filter(([v, code], i) => cands.findIndex(([w]) => w === v) === i && cands.every(([w, c]) => w !== v || c === code))
        .map(([v, code]) => ({ code, trou, valeur: String(v) }));
});

// ─── Carte 1 : Série flash (4 lignes saisie) ──────────────────────────────

const CONSIGNE_FLASH = '<p><b>Calcule.</b> Un résultat négatif s\'écrit avec le tiret (ex. : -8) ; s\'il est nul, écris 0.</p>';

const ligneFlash = (ecriture, resultat) => `$${ecriture} = {{${resultat}}}$`;

const assemblerFlash = (items) => {
    const lignes = items.map((it) => ligneFlash(verifierOperation(it.op, it.forme, it.op.termes.length > 2 ? MAX_PLUSIEURS : MAX_DEUX), it.resultat));
    const payload = makeTexteTrou(lignes.join('\n'), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items: items.map(({ op, forme, geste, resultat }) => ({ ...op, forme, geste, resultat })) };
    payload.contenu = { ...payload.contenu, erreurs: erreursFlash(items) };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        const items = [
            { op: tirerSommeParentheses(), forme: 'parentheses', geste: 'somme_parentheses' },
            { op: tirerSommeSimplifiee(), forme: 'simplifiee', geste: 'somme_simplifiee' },
            { op: tirerDifference(), forme: 'parentheses', geste: 'difference' },
            { op: tirerSommePlusieurs(), forme: randomChoice(['parentheses', 'simplifiee']), geste: 'somme_plusieurs' },
        ].map((it) => ({ ...it, resultat: valeur(it.op) }));
        if (new Set(items.map((it) => it.resultat)).size < 4) continue;
        return assemblerFlash(items);
    }
    return assemblerFlash([
        { op: { termes: [-7, -5], ops: ['+'] }, forme: 'parentheses', geste: 'somme_parentheses' },
        { op: { termes: [-9, 4], ops: ['+'] }, forme: 'simplifiee', geste: 'somme_simplifiee' },
        { op: { termes: [7, -5], ops: ['-'] }, forme: 'parentheses', geste: 'difference' },
        { op: { termes: [-4, 9, -6], ops: ['+', '+'] }, forme: 'simplifiee', geste: 'somme_plusieurs' },
    ].map((it) => ({ ...it, resultat: valeur(it.op) })));
};

// ─── Carte 2 : QCM « Quel résultat ? » ────────────────────────────────────
//
// Six formes, a et b entiers de 2..15 distincts. Les quatre choix sont
// TOUJOURS ±(a + b) et ±(a − b) : selon la forme, la clé est l'un d'eux et
// les trois autres portent le code de l'erreur qui y conduit.

const FORMES_QCM = {
    // −a + b (DEPP q.5) : clé b − a
    negatif_plus_positif: {
        op: (a, b) => ({ termes: [-a, b], ops: ['+'] }),
        forme: () => 'simplifiee',
        codes: (a, b) => [[-(a + b), 'distances_ajoutees'], [a + b, 'signes_ignores'], [a - b, 'signe_inverse']],
    },
    // a + (−b) : clé a − b
    positif_plus_negatif: {
        op: (a, b) => ({ termes: [a, -b], ops: ['+'] }),
        forme: () => 'parentheses',
        codes: (a, b) => [[-(a + b), 'distances_ajoutees'], [a + b, 'signes_ignores'], [b - a, 'signe_inverse']],
    },
    // (−a) + (−b) ou −a − b : clé −(a + b)
    deux_negatifs: {
        op: (a, b) => ({ termes: [-a, -b], ops: ['+'] }),
        forme: () => randomChoice(['parentheses', 'simplifiee']),
        codes: (a, b) => [[a + b, 'signes_ignores'], [-Math.abs(a - b), 'ecart_au_lieu_de_somme'], [Math.abs(a - b), 'ecart_sans_signe']],
    },
    // a − (−b) (DEPP q.14) : clé a + b
    double_moins: {
        op: (a, b) => ({ termes: [a, -b], ops: ['-'] }),
        forme: () => 'parentheses',
        codes: (a, b) => [[a - b, 'moins_avale'], [-(a + b), 'distances_ajoutees'], [b - a, 'ordre_inverse']],
    },
    // −a − (−b) : clé b − a
    double_moins_negatif: {
        op: (a, b) => ({ termes: [-a, -b], ops: ['-'] }),
        forme: () => 'parentheses',
        codes: (a, b) => [[-(a + b), 'moins_avale'], [a + b, 'signes_ignores'], [a - b, 'signe_inverse']],
    },
    // a − b avec a < b : clé a − b (négative)
    petit_moins_grand: {
        op: (a, b) => ({ termes: [Math.min(a, b), Math.max(a, b)], ops: ['-'] }),
        forme: () => 'simplifiee',
        codes: (a, b) => [[Math.abs(a - b), 'signe_inverse'], [-(a + b), 'distances_ajoutees'], [a + b, 'signes_ignores']],
    },
};

const assemblerQcm = (op, forme, nom, a, b, faux) => {
    const bon = valeur(op);
    const valeurs = new Set([bon, ...faux.map((x) => x.valeur)]);
    if (valeurs.size !== 4) throw new Error(`gen_1091 : choix en collision (${nom}, ${a}, ${b})`);
    const ecriture = verifierOperation(op, forme, MAX_DEUX);
    const payload = makeQcm(
        [String(bon), ...faux.map((x) => String(x.valeur))],
        String(bon),
        `<p><b>Combien vaut ${tex(ecriture)} ?</b></p>`,
        opts(L_QCM),
        false,
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...op, forme, nom, a, b, resultat: bon, distracteurs: faux.map(({ code }) => ({ code })) };
    payload.contenu = { ...payload.contenu, erreurs: faux.map((x) => ({ code: codeQcm(x.code, nom, forme), valeur: String(x.valeur) })).filter((e) => e.code) };
    return payload;
};

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const nom = randomChoice(Object.keys(FORMES_QCM));
        const f = FORMES_QCM[nom];
        const a = randInt(2, 15);
        const b = randInt(2, 15);
        if (a === b) continue;
        const op = f.op(a, b);
        if (estSacrifiee(enSomme(op))) continue;
        return assemblerQcm(op, f.forme(), nom, a, b, f.codes(a, b).map(([v, code]) => ({ valeur: v, code })));
    }
    const f = FORMES_QCM.double_moins;
    return assemblerQcm(f.op(7, 5), 'parentheses', 'double_moins', 7, 5, f.codes(7, 5).map(([v, code]) => ({ valeur: v, code })));
};

// ─── Carte 3 : Écrire sans parenthèses (drag) ─────────────────────────────

const CONSIGNE_SENS = '<p><b>Écris cette somme sans parenthèses</b> (sans calculer le résultat).</p>';

/** Étiquette d'un terme en écriture simplifiée : « + 5 », « - 8 ». */
const etiquette = (t) => `${t < 0 ? '-' : '+'} ${Math.abs(t)}`;

const assemblerSens = (op) => {
    const ecriture = verifierOperation(op, 'parentheses', MAX_PLUSIEURS);
    const simples = enSomme(op);
    const phrase = `${ecriture} = ${premier(simples[0])} {{${etiquette(simples[1])}}} {{${etiquette(simples[2])}}}`;
    const leurres = filtrerDistracteurs(phrase, [etiquette(-simples[1]), etiquette(-simples[2])]);
    if (leurres.length !== 2) return null;
    const payload = makeTexteTrou(phrase, CONSIGNE_SENS, { ...opts(L_SENS), mode: 'drag', distracteurs: leurres });
    payload.contenu = { ...payload.contenu, explications: { signe_contraire: EXPLICATIONS.signe_contraire, moins_avale: EXPLICATIONS.moins_avale } };
    payload._meta.params = { ...op, simples, resultat: valeur(op), distracteurs: leurres.map(() => ({ code: 'signe_contraire' })) };
    // signe_contraire n'est le moins avalé que pour un terme négatif entre parenthèses
    const erreurs = [1, 2].filter((k) => op.termes[k] < 0 && leurres.includes(etiquette(-simples[k])))
        .map((k) => ({ code: commun('signe_contraire'), trou: k - 1, valeur: etiquette(-simples[k]) }));
    payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

const genSens = () => {
    for (let essai = 0; essai < 80; essai++) {
        const termes = [entier(MAX_PLUSIEURS), entier(MAX_PLUSIEURS), entier(MAX_PLUSIEURS)];
        if (new Set(termes.map(Math.abs)).size < 3) continue;
        const ops = [randomChoice(['+', '-']), randomChoice(['+', '-'])];
        // au moins un « − (−x) » : le cas que la DEPP donne raté une fois sur deux
        if (!ops.some((o, i) => o === '-' && termes[i + 1] < 0)) continue;
        const payload = assemblerSens({ termes, ops });
        if (payload) return payload;
    }
    return assemblerSens({ termes: [-3, -5, -8], ops: ['-', '+'] });
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix, toujours les quatre nombres ±(a + b) et ±(a − b), a ≠ b dans 2..15 : distincts par construction, la clé est l\'un d\'eux et chaque autre porte le code de l\'erreur qui y mène',
        'aucune option inventée à l\'assemblage : une collision est un bug (throw), pas un nouveau tirage',
        'les termes sacrifiés {−3 ; +8} ne sortent jamais, sous aucune écriture',
    ],
    qcm_unique: {
        support: 'une somme ou différence de deux entiers, six formes en rotation : −a + b, a + (−b), (−a) + (−b) ou −a − b, a − (−b), −a − (−b), a − b (a < b)',
        familles: {
            distances_ajoutees: '−(a + b) : les deux nombres comptés négatifs (DEPP 4e 2023 : −5 + 7 → −12, 13 % ; 7 − (−5) → −12, 17 %)',
            signes_ignores: 'a + b : les signes oubliés (−5 + 7 → 12, 7 %)',
            signe_inverse: 'la bonne distance, le mauvais signe (−5 + 7 → −2, 8 %)',
            moins_avale: '7 − (−5) → 7 − 5 = 2 : le deuxième moins avalé (21 %, la principale des différences)',
            ordre_inverse: '7 − (−5) → 5 − 7 = −2 (15 %)',
            ecart_au_lieu_de_somme: '−3 − 5 → −2 : l\'écart au lieu de la somme de deux négatifs (exemple prof)',
            ecart_sans_signe: '−3 − 5 → 2 : l\'écart, et le signe perdu',
        },
    },
    sens_texte_trou_drag: 'deux leurres = les deux étiquettes de signe contraire (code signe_contraire) ; valeurs absolues distinctes, donc jamais deux étiquettes identiques',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    resultat_qcm_unique: genQcm,
    sens_texte_trou_drag: genSens,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'resultat', label: L_QCM, etapes: 1, formatsList: [{ key: 'resultat_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_SENS, etapes: 1, formatsList: [{ key: 'sens_texte_trou_drag', label: 'Glisser-déposer' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
